import { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Slide,
  Typography,
  IconButton,
} from "@mui/material";
import Button from "../../common/Button";
import { TransitionProps } from "@mui/material/transitions";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import React from "react";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  fetchUsersByUserInput,
  useUserReducer,
} from "../../../redux/slice/userSlice";
import {
  acknowledgeNotification,
  assignNotification,
  notificationAttachmentDTO,
} from "../../../redux/slice/inbox/createNotificationSlice";
import {
  Visibility,
  AssignmentIndOutlined,
  CheckCircleOutline,
  Close,
  AttachFileOutlined,
} from "@mui/icons-material";
import { fetchOrderDocumentsByIds } from "../../../redux/slice/order/OrderSlice";
import {
  setSingleAlertObj,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useModalStyles } from "../../common/useModalStyles";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface AssignPopupProps {
  mode: string;
  canAssign: boolean;
  isAcknowledged: boolean;
  attachments: notificationAttachmentDTO[];
  open: boolean;
  onClose: () => void;
  onSubmitAssign: (data: any) => void;
  notification: {
    notificationId: number;
    subject: string;
    message: string;
    category: string;
    time?: string;
    senderId?: number;
    notificationAcks?: any[];
  };
}

const AssignPopup: React.FC<AssignPopupProps> = ({
  mode,
  canAssign,
  isAcknowledged,
  attachments,
  open,
  onClose,
  onSubmitAssign,
  notification,
}) => {
  const [userOptions, setUserOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const { userInfo } = useAuthSlice();
  const dispatch = useAppDispatch();
  const { fetchUsersByQuery } = useUserReducer();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();
  const [pdfContent, setPDFContent] = React.useState<string>("");
  const [showPDF, setShowPDF] = React.useState<{
    isShow: boolean;
    title: string;
    fileName: string;
  }>({ isShow: false, title: "", fileName: "" });
  const { documentDataFromCommonApi } = useCommonReducer();

  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  useEffect(() => {
    dispatch(fetchUsersByUserInput({ searchQuery: "", skipLoader: true }));
  }, []);

  useEffect(() => {
    if (fetchUsersByQuery && fetchUsersByQuery?.length > 0) {
      const ackUserIds =
        notification.notificationAcks?.map((ack) => parseInt(ack.ackUsrId)) ||
        [];
      const senderId = notification.senderId;
      const options = fetchUsersByQuery
        .filter(
          (item) =>
            parseInt(item.userId) !== userInfo.userId &&
            !ackUserIds.includes(parseInt(item.userId)) &&
            Number(item.userId) !== senderId
        )
        .map((item) => ({
          value: parseInt(item.userId),
          label: `${item.firstName} ${
            item.middleName ? item.middleName + " " : ""
          }${item.lastName} (${item.email})`,
        }));
      setUserOptions(options);
    }
  }, [fetchUsersByQuery]);

  const onSubmit = async (data: any) => {
    try {
      if (mode === "assign") {
        const assignData = {
          notificationId: notification.notificationId,
          assignedUserId: data.assignedUserId,
        };
        const response = await dispatch(assignNotification(assignData));
        onSubmitAssign(response.payload.data);
      } else {
        const notificationData = {
          notificationId: notification.notificationId,
          comment: "",
        };
        const response = await dispatch(
          acknowledgeNotification(notificationData)
        ).unwrap();

        onSubmitAssign(response.data);
      }

      onClose();
    } catch (err) {
      console.log("Error assigning: ", err);
    }
  };

  const handleActionPreview = async (
    attachment: notificationAttachmentDTO,
    type: string
  ) => {
    const documentId = attachment.documentId;
    if (documentId) {
      const actionResult = await dispatch(
        fetchOrderDocumentsByIds([documentId])
      );
      const documentData = actionResult.payload;

      if (documentData?.content) {
        setPDFContent(documentData.content);
        setShowPDF({
          isShow: true,
          title: documentData.patientName,
          fileName: documentData.documentName,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Requested Document has not found under server location!",
            alertType: "info",
          })
        );
      }
    }
  };

  const isAssign = mode === "assign";
  const isView = mode === "view";
  const accent = isAssign
    ? tokens.accent
    : isView
      ? "#00B8D9"
      : "#22C55E";
  const iconBg = isAssign
    ? isDark
      ? `${tokens.accent}1a`
      : "#F0FDFA"
    : isView
      ? isDark
        ? "rgba(0,184,217,0.12)"
        : "#ECFEFF"
      : isDark
        ? "rgba(34,197,94,.12)"
        : "#F0FDF4";
  const iconBdr = isAssign
    ? isDark
      ? `${tokens.accent}33`
      : "#99F6E4"
    : isView
      ? isDark
        ? "rgba(0,184,217,0.28)"
        : "#A5F3FC"
      : isDark
        ? "rgba(34,197,94,.22)"
        : "#A7F3D0";
  const Icon = isAssign
    ? AssignmentIndOutlined
    : isView
      ? Visibility
      : CheckCircleOutline;
  const headerTitle = isAssign
    ? "Assign message"
    : isView
      ? "View Notification"
      : "Acknowledge";
  const submitLabel = isAssign ? "Assign" : "Acknowledge";
  const attachBg = isDark ? "rgba(255,255,255,.03)" : tokens.cardBg;
  const attachBdr = isDark ? "rgba(255,255,255,.06)" : tokens.border;
  const messageCardBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.025)";

  const cancelActionBtnSx = {
    ...cancelBtnSx,
    color: tokens.error,
    borderColor: "rgba(239,68,68,0.4)",
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "&:hover": {
      ...cancelBtnSx["&:hover"],
      color: tokens.error,
      "& .MuiTypography-root": { color: "inherit" },
    },
  };

  const assignActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    backgroundColor: accent,
    background: isAssign
      ? primaryBtnSx.background
      : `linear-gradient(135deg, ${accent} 0%, ${accent} 100%)`,
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "&:hover": {
      color: "#fff",
      backgroundColor: accent,
      opacity: 0.92,
      "& .MuiTypography-root": { color: "#fff" },
    },
  };

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        aria-describedby="assign-dialog-description"
        fullWidth
        maxWidth={isView ? "sm" : "xs"}
        PaperProps={{ elevation: 0, sx: paperSx() }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? "rgba(0,0,0,0.72)" : "rgba(15,23,42,0.45)",
            backdropFilter: "blur(3px)",
          },
        }}
      >
        {showPDF.isShow && (
          <PDFViewer
            isOpen={showPDF.isShow}
            onClose={() => {
              setShowPDF({ isShow: false, title: "", fileName: "" });
            }}
            title={showPDF.title}
            fileName={showPDF.fileName}
            base64String={
              documentDataFromCommonApi?.content
                ? documentDataFromCommonApi?.content
                : pdfContent
            }
          />
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
        >
          <Box sx={headerSx}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "11px",
                background: iconBg,
                border: `1px solid ${iconBdr}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 20, color: accent }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: tokens.textPrimary,
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                {headerTitle}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: tokens.textSecondary,
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {notification.subject}
              </Typography>
            </Box>

            <IconButton size="small" onClick={onClose} sx={closeIconSx}>
              <Close sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box sx={{ ...bodySx, gap: 2 }}>
            {isAssign ? (
              <Typography
                sx={{ fontSize: "0.8rem", color: tokens.textSecondary, lineHeight: 1.7 }}
              >
                Assigning a message means linking or directing a specific message
                (text, notification, or communication) to a person, process, or
                variable, depending on the use case.
              </Typography>
            ) : (
              <Box
                sx={{
                  px: 1.75,
                  py: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${attachBdr}`,
                  backgroundColor: messageCardBg,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: tokens.textSecondary,
                    mb: 0.85,
                  }}
                >
                  Message
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.84rem",
                    color: tokens.textPrimary,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {notification.message}
                </Typography>
              </Box>
            )}

            {attachments.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: tokens.textSecondary,
                    mb: 0.25,
                  }}
                >
                  Attachments
                </Typography>

                {attachments.map((attachment, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: "9px",
                      background: attachBg,
                      border: `1px solid ${attachBdr}`,
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                      "&:hover": {
                        borderColor: `${accent}66`,
                        backgroundColor: isDark
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(15,23,42,0.04)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "7px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: `${accent}14`,
                          border: `1px solid ${accent}33`,
                          color: accent,
                        }}
                      >
                        <AttachFileOutlined sx={{ fontSize: 14 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          color: tokens.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {attachment.documentName}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      className="hover-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionPreview(attachment, "document");
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "7px",
                        color: tokens.textSecondary,
                        flexShrink: 0,
                        border: `1px solid ${attachBdr}`,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          background: `${accent}18`,
                          borderColor: `${accent}55`,
                          color: accent,
                        },
                      }}
                    >
                      <Visibility sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {notification.time && (
              <Box display="flex" justifyContent="flex-end">
                <Typography sx={{ fontSize: "0.68rem", color: tokens.textSecondary }}>
                  {notification.time}
                </Typography>
              </Box>
            )}

            {mode === "assign" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography
                  sx={{ fontSize: "0.78rem", fontWeight: 600, color: tokens.textPrimary }}
                >
                  Assign to
                </Typography>
                <Box sx={dropdownWrapSx(!!errors?.assignedUserId)}>
                  <CustomDropdownField
                    name="assignedUserId"
                    label="Assign To"
                    options={userOptions}
                    control={control}
                    rules={{ required: "Assigned user is required" }}
                  />
                </Box>
                {errors?.assignedUserId && (
                  <Typography sx={{ fontSize: "0.7rem", color: tokens.error, mt: -0.25 }}>
                    {errors.assignedUserId.message as string}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              ...footerSx,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.25,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={onClose}
              removeUpperCase
              sx={cancelActionBtnSx}
            >
              Cancel
            </Button>
            {mode === "assign" && (
              <Button
                type="submit"
                variant="contained"
                disableElevation
                removeUpperCase
                sx={assignActionBtnSx}
              >
                {submitLabel}
              </Button>
            )}
          </Box>
        </form>
      </Dialog>
    </>
  );
};

export default AssignPopup;
